import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createSearchSession } from '../../palette/runSearch';
import { getProviders, getProvider, subscribeRegistry } from '../../palette/registry';
import { recentRefs, subscribeFrecency } from '../../palette/frecency';
import { parseRef, refOf } from '../../palette/types';
import { useTabsStore } from '../../store/tabsStore';

/** The provider whose group the current page boosts (+60, §3.4). */
export const PAGE_PROVIDER = {
  'documents.panel': 'documents',
  'task-manager.panel': 'tasks',
  'chat.panel': 'people',
  collections: 'requests',
  'calendar.panel': 'calendar',
  'projects.panel': 'projects',
};

/** Refs of the open tabs (+30, §3.4). */
export function openTabRefs(state = useTabsStore.getState()) {
  const refs = new Set();
  for (const t of state.documentTabs || []) if (t.documentId) refs.add(refOf('documents', t.documentId));
  for (const t of state.taskManagerTabs || []) if (t.boardId) refs.add(refOf('boards', t.boardId));
  for (const t of state.requestTabs || []) if (t.originalId != null) refs.add(refOf('requests', t.originalId));
  return refs;
}

/** Registry changes (a provider registering late) re-render the palette. */
export function useRegistryVersion() {
  const [v, setV] = useState(0);
  useEffect(() => subscribeRegistry(() => setV((x) => x + 1)), []);
  return v;
}

/**
 * Runs the orchestrator for the current query. The run happens in a LAYOUT
 * effect, so `local-sync` results are on screen in the same frame as the
 * keystroke that asked for them (≤ 16 ms budget, §3.3).
 */
export function usePaletteSearch({ open, query, getCtx, onLanded }) {
  const [state, setState] = useState(null);
  const getCtxRef = useRef(getCtx);
  getCtxRef.current = getCtx;
  const landedRef = useRef(onLanded);
  landedRef.current = onLanded;
  const session = useMemo(
    () =>
      createSearchSession({
        getProviders,
        getCtx: () => getCtxRef.current(),
        rankOptions: () => {
          const ctx = getCtxRef.current();
          return { pageProvider: PAGE_PROVIDER[ctx.activeTab] || null, openRefs: openTabRefs() };
        },
        onUpdate: (s) => {
          setState(s);
          if (s.landed) landedRef.current?.(s.landed);
        },
      }),
    []
  );
  useEffect(() => () => session.dispose(), [session]);

  const active = open && query && (query.scope || query.text);
  const key = active ? `${query.scope || ''}\u0000${query.text}` : null;
  useLayoutEffect(() => {
    if (!key) {
      session.cancel();
      setState(null);
      return;
    }
    session.run(query);
    // `query` is derived from `key`; re-running on its identity would double-run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, session]);
  return state;
}

/**
 * Recent (§2.4): the last N items opened through ANY surface, hydrated from
 * frecency refs through each provider's `recent(ids)` — one small call per
 * provider, nothing loaded on open beyond that.
 */
export function usePaletteRecent({ open, limit, getCtx }) {
  const [rows, setRows] = useState([]);
  const [tick, setTick] = useState(0);
  useEffect(() => subscribeFrecency(() => setTick((x) => x + 1)), []);
  useEffect(() => {
    if (!open) {
      setRows([]);
      return undefined;
    }
    let cancelled = false;
    const refs = recentRefs(limit * 2);
    const byProvider = new Map();
    for (const ref of refs) {
      const r = parseRef(ref);
      if (!r) continue;
      if (!byProvider.has(r.provider)) byProvider.set(r.provider, []);
      byProvider.get(r.provider).push(r.id);
    }
    const ctx = getCtx();
    Promise.all(
      [...byProvider.entries()].map(async ([pid, ids]) => {
        const p = getProvider(pid);
        if (!p || typeof p.recent !== 'function') return [];
        try {
          if (!p.available(ctx)) return [];
          return (await p.recent(ids, ctx)) || [];
        } catch {
          return [];
        }
      })
    ).then((lists) => {
      if (cancelled) return;
      const byRef = new Map();
      for (const list of lists) for (const r of list) byRef.set(refOf(r.provider, r.id), r);
      setRows(refs.map((ref) => byRef.get(ref)).filter(Boolean).slice(0, limit));
    });
    return () => {
      cancelled = true;
    };
    // getCtx is read once per open; the tick re-hydrates after "remove from recent".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, limit, tick]);
  return rows;
}
